import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import {Folio} from "crqlar-react-common/dist/models/billing/folio.model";
import {formatDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {DigitalSigningStatusElement} from "../../../common/uiElements/DigitalSigningStatusElement";
import DigitalSigningDetailsDialog from "../../../digitalSigning/details/DigitalSigningDetailsDialog";
import {DigitalSigningStatusView} from "../../../digitalSigning/digitalSigning.model";
import {isSignedDigitalSigning} from "../../../digitalSigning/share/digitalSigningFlow.util";
import {useLatestFolioDigitalSigning} from "../../../digitalSigning/share/useLatestDigitalSigning";
import {useFolioFormat} from "./editor/folioFormat";

// When it got to its status: confirmed, signed - else when its link was issued
const momentOf = ({status, confirmedAt, signedAt, issuedAt}: DigitalSigningStatusView) => {
    if (status === 'CONFIRMED') return confirmedAt;
    if (status === 'SIGNED') return signedAt;
    return issuedAt;
};

interface Props {
    folio: Folio
}

/**
 * Basic info's row of the folio's latest signing, whatever its status - only a folio of a
 * reservation has one: its status, when it got there and, once signed, the tip. View shows what the
 * guest signed over the page, for read-only users too.
 */
const FolioDigitalSigningRow = ({folio}: Props) => {
    const {t} = useTranslation();
    const {data: latest} = useLatestFolioDigitalSigning(folio);
    const format = useFolioFormat(latest?.currency || latest?.tip?.currency || folio.currency || 'EUR');
    const [detailsOpen, setDetailsOpen] = useState(false);

    if (!latest) return null;

    // The element says the status; the text when, and the tip once the guest signed
    const tipCents = latest.tip?.amountCents ?? 0;
    const tip = tipCents > 0 ? t('tip {{amount}}', {amount: format.money(tipCents)}) : t('no tip');
    const summary = [formatDateTime('dd.MM.yyyy HH:mm', momentOf(latest)), isSignedDigitalSigning(latest.status) && tip]
        .filter(Boolean)
        .join(' · ');

    return (
        <>
            <Property
                title={t('Signature')}
                tooltip={t("The folio's latest signature, with the tip.")}
                // Outside the read-only lock: viewing it changes nothing
                actions={(
                    <Button id={'btn-view-digital-signing'} sx={{minWidth: 'fit-content'}} onClick={() => setDetailsOpen(true)}>
                        {t('View')}
                    </Button>
                )}
            >
                <>
                    <DigitalSigningStatusElement status={latest.status}/>
                    {!!summary && <Typography variant={'body2'} color={'text.secondary'}>{summary}</Typography>}
                </>
            </Property>

            {detailsOpen && <DigitalSigningDetailsDialog id={latest.id} open onClose={() => setDetailsOpen(false)}/>}
        </>
    );
};

export default FolioDigitalSigningRow;
